import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroBackdrop from "@/components/HeroBackdrop";
import HeroActions from "@/components/HeroActions";
import { useT } from "@/i18n/LanguageProvider";
import { ReferenceNote } from "@/components/ReferenceNote";
import { newDossierReference } from "@/lib/reference";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { prenom: "", nom: "", email: "", liens: "", contexte: "" };

function isNetlifyHost() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return (
    host.endsWith(".netlify.app") ||
    host === "horsindex.com" ||
    host.endsWith(".horsindex.com")
  );
}

// Anti-spam invisible : le jeton serveur est résolu automatiquement côté
// client (la question est une simple addition), sans étape visible.
function computeAnswer(question) {
  const m = /(\d+)\s*\+\s*(\d+)/.exec(question || "");
  return m ? String(Number(m[1]) + Number(m[2])) : "";
}

export default function Diagnostic() {
  const { t, lang } = useT();
  const [form, setForm] = useState(EMPTY);
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState("");
  const [challenge, setChallenge] = useState(null);

  const loadChallenge = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contact-challenge`);
      setChallenge(res.data);
    } catch {
      setChallenge(null);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    if (!isNetlifyHost()) loadChallenge();
  }, [loadChallenge]);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    setLoading(true);
    const ref = newDossierReference();
    try {
      if (isNetlifyHost()) {
        const fd = new URLSearchParams();
        fd.append("form-name", "contact");
        fd.append("origine", "audit-initial");
        fd.append("reference", ref);
        fd.append("prenom", form.prenom);
        fd.append("nom", form.nom);
        fd.append("email", form.email);
        fd.append("liens", form.liens);
        fd.append("message", form.contexte);
        fd.append("lang", lang || "fr");
        const res = await fetch("/", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: fd.toString(),
        });
        if (!res.ok) throw new Error(`Netlify Forms returned ${res.status}`);
      } else {
        await axios.post(`${API}/diagnostic`, {
          ...form,
          lang: lang || "fr",
          reference: ref,
          website: honeypot,
          captcha_token: challenge?.token || "",
          captcha_answer: computeAnswer(challenge?.question),
        });
      }
      setReference(ref);
      setSubmitted(true);
      toast.success(t("diagnostic.toastSuccess"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error(t("diagnostic.toastError"));
      if (!isNetlifyHost()) loadChallenge();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(EMPTY);
    setSubmitted(false);
    setReference("");
    if (!isNetlifyHost()) loadChallenge();
  };

  return (
    <div data-testid="page-diagnostic" className="bg-[#FAF9F7] text-[#181A1D] min-h-screen">
      <Navbar />
      <main className="relative">
        {/* Header */}
        <section className="relative bg-[#FAF9F7] grain overflow-hidden">
          <div className="hero-blur-lines absolute inset-0 opacity-50" />
          <HeroBackdrop variant="focal" />
          <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-10 pt-36 lg:pt-44 pb-16 lg:pb-20">
            <Link
              to="/"
              data-testid="diag-back-home"
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#666A70] hover:text-[#181A1D] mb-10 transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.5} /> {t("common.back")}
            </Link>

            <p className="label-eyebrow mb-6">{t("diagnostic.pageEyebrow")}</p>
            <h1
              data-testid="diag-title"
              className="font-serif font-normal text-[#181A1D] text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.04] text-balance max-w-4xl"
            >
              {t("diagnostic.pageTitlePre")}
              <em className="italic font-normal">{t("diagnostic.pageTitleEm")}</em>
              {t("diagnostic.pageTitlePost")}
            </h1>
            <div aria-hidden="true" data-testid="h1-marker" className="h1-marker mt-8" />
            <p className="mt-8 max-w-2xl text-base lg:text-lg text-[#52565C] font-normal leading-relaxed">
              {t("diagnostic.intro")}
            </p>
            <HeroActions />
          </div>
          <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10">
            <div className="h-px bg-[#E2DEDA]" />
          </div>
        </section>

        {/* Form / Success */}
        <section className="relative bg-[#FAF9F7] py-16 lg:py-24">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
            {submitted ? (
              <div
                data-testid="diag-success"
                className="border border-[#E2DEDA] bg-white rounded-lg p-12 lg:p-16 max-w-3xl mx-auto flex flex-col items-start gap-8"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-md border border-[#181A1D]">
                  <Check size={22} strokeWidth={1.5} className="text-[#181A1D]" />
                </div>
                <h2 className="font-serif text-4xl lg:text-5xl text-[#181A1D] font-normal leading-tight">
                  {t("diagnostic.successTitle")}
                </h2>
                <p className="text-[#5A5E64] font-normal leading-relaxed max-w-xl">
                  {t("diagnostic.successText")}
                </p>
                <ReferenceNote reference={reference} testId="diag-reference" className="w-full max-w-xl" />
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <Link to="/" className="btn-primary" data-testid="diag-back-cta">
                    {t("diagnostic.backHome")}
                  </Link>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-ghost"
                    data-testid="diag-new-request"
                  >
                    {t("diagnostic.newRequest")}
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="diag-form"
                className="max-w-xl mx-auto space-y-9"
              >
                {/* Honeypot */}
                <p hidden aria-hidden="true">
                  <label>
                    Do not fill:{" "}
                    <input
                      name="bot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </label>
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-9">
                  <div>
                    <label
                      htmlFor="diag-prenom"
                      className="block text-[14px] font-medium text-[#181A1D] mb-2"
                    >
                      Prénom <span className="text-[#B83A34]">*</span>
                    </label>
                    <input
                      id="diag-prenom"
                      type="text"
                      name="prenom"
                      required
                      value={form.prenom}
                      onChange={onChange}
                      data-testid="diag-input-prenom"
                      className="input-line"
                      autoComplete="given-name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="diag-nom"
                      className="block text-[14px] font-medium text-[#181A1D] mb-2"
                    >
                      Nom <span className="text-[#B83A34]">*</span>
                    </label>
                    <input
                      id="diag-nom"
                      type="text"
                      name="nom"
                      required
                      value={form.nom}
                      onChange={onChange}
                      data-testid="diag-input-nom"
                      className="input-line"
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="diag-email"
                    className="block text-[14px] font-medium text-[#181A1D] mb-2"
                  >
                    Adresse e-mail <span className="text-[#B83A34]">*</span>
                  </label>
                  <input
                    id="diag-email"
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={onChange}
                    data-testid="diag-input-email"
                    className="input-line"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label
                    htmlFor="diag-liens"
                    className="block text-[14px] font-medium text-[#181A1D] mb-2"
                  >
                    Lien(s) ou entité concernée{" "}
                    <span className="text-[13px] font-normal text-[#7C8086]">
                      (facultatif)
                    </span>
                  </label>
                  <input
                    id="diag-liens"
                    type="text"
                    name="liens"
                    value={form.liens}
                    onChange={onChange}
                    data-testid="diag-input-liens"
                    className="input-line"
                    placeholder="URL, nom de société ou autre élément d'identification"
                  />
                </div>

                <div>
                  <label
                    htmlFor="diag-contexte"
                    className="block text-[14px] font-medium text-[#181A1D] mb-2"
                  >
                    Décrivez brièvement votre situation{" "}
                    <span className="text-[#B83A34]">*</span>
                  </label>
                  <textarea
                    id="diag-contexte"
                    name="contexte"
                    required
                    rows={6}
                    value={form.contexte}
                    onChange={onChange}
                    data-testid="diag-input-contexte"
                    className="input-line resize-y min-h-[140px]"
                    placeholder="Indiquez ce qui apparaît en ligne et ce que vous souhaitez faire examiner."
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="diag-submit"
                    className="btn-primary disabled:opacity-50 w-full sm:w-auto justify-center"
                  >
                    {loading ? "Envoi en cours…" : "Envoyer ma demande"}
                    <ArrowRight size={14} strokeWidth={1.5} />
                  </button>
                  <p className="mt-5 text-[13px] leading-relaxed text-[#7C8086]">
                    Les informations transmises sont utilisées uniquement pour
                    examiner votre demande.{" "}
                    <Link
                      to="/politique-confidentialite"
                      data-testid="diag-privacy-link"
                      className="text-[#5A5E64] underline decoration-[#D8D3CD] underline-offset-4 hover:text-[#181A1D] hover:decoration-[#E94B3C] transition-colors"
                    >
                      Voir la politique de confidentialité
                    </Link>
                    .
                  </p>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
